import { inject, prop } from "fw";
import moment from "moment-timezone";

import "vue2-daterange-picker/dist/vue2-daterange-picker.css";

export type Range = {
    startDate: Date;
    endDate: Date;
};

export const formatDate = (date: Date): string => {
    return date
        .toISOString()
        .replace(/:/g, String.raw`\:`)
        .replace(/\..*/, "");
};

type DefaultRange = {
    label: string;
    value: number;
    getMarker?: (formatter: (date: Date) => string, timezone: string) => string | [string, string];
    getRange?: (timezone: string) => Range;
};

export enum DefaultDates {
    any,
    today,
    yesterday,
    week,
    month,
    year
}

export const DEFAULT_RANGES: DefaultRange[] = [
    {
        label: "Any Dates",
        value: DefaultDates.any,
        getMarker: () => null,
        getRange: () => null
    },
    {
        label: "Today",
        value: DefaultDates.today,
        getMarker: (formatter, timezone: string) => {
            timezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
            const today = moment().tz(timezone).format("YYYY-MM-DD");
            const tomorrow = moment().tz(timezone).add(1, "days").format("YYYY-MM-DD");
            // '[' = inclusive start, '}' = exclusive end (end date not included)
            return `[${today} TO ${tomorrow}}^\"${timezone}\"`;
        },
        getRange: (timezone: string) => {
            timezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
            const today = moment().tz(timezone);
            const tomorrow = moment().tz(timezone).add(1, "days");
            return { startDate: today.toDate(), endDate: tomorrow.toDate() };
        }
    },
    {
        label: "Yesterday",
        value: DefaultDates.yesterday,
        getMarker: (formatter, timezone: string) => {
            timezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
            const yesterday = moment().tz(timezone).add(-1, "days").format("YYYY-MM-DD");
            const today = moment().tz(timezone).format("YYYY-MM-DD");
            // '[' = inclusive start, '}' = exclusive end (end date not included)
            return `[${yesterday} TO ${today}}^\"${timezone}\"`;
        },
        getRange: (timezone: string) => {
            timezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
            const yesterday = moment().tz(timezone).add(-1, "days");
            const today = moment().tz(timezone);
            return { startDate: yesterday.toDate(), endDate: today.toDate() };
        }
    },
    {
        label: "Last 7 days",
        value: DefaultDates.week,
        getMarker: (formatter, timezone: string) => {
            timezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
            const dateStart = moment().tz(timezone).add(-7, "days").format("YYYY-MM-DD");
            const today = moment().tz(timezone).format("YYYY-MM-DD");
            return `[${dateStart} TO ${today}]^\"${timezone}\"`;
        },
        getRange: (timezone: string) => {
            timezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
            const dateStart = moment().tz(timezone).add(-7, "days");
            const today = moment().tz(timezone);
            return { startDate: dateStart.toDate(), endDate: today.toDate() };
        }
    },
    {
        label: "This month",
        value: DefaultDates.month,
        getMarker: (formatter, timezone: string) => {
            timezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
            const dateStart = moment().tz(timezone).startOf("month").format("YYYY-MM-DD");
            const today = moment().tz(timezone).format("YYYY-MM-DD");

            return `[${dateStart} TO ${today}]^\"${timezone}\"`;
        },
        getRange: (timezone: string) => {
            timezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
            const dateStart = moment().tz(timezone).startOf("month");
            const today = moment().tz(timezone);
            return { startDate: dateStart.toDate(), endDate: today.toDate() };
        }
    },
    {
        label: "This year",
        value: DefaultDates.year,
        getMarker: (formatter, timezone: string) => {
            timezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
            const dateStart = moment().tz(timezone).startOf("year").format("YYYY-MM-DD");
            const today = moment().tz(timezone).format("YYYY-MM-DD");

            return `[${dateStart} TO ${today}]^\"${timezone}\"`;
        },
        getRange: (timezone: string) => {
            timezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
            const dateStart = moment().tz(timezone).startOf("year");
            const today = moment().tz(timezone);
            return { startDate: dateStart.toDate(), endDate: today.toDate() };
        }
    }
];

const INITIAL_RANGE: Range = {
    startDate: null,
    endDate: null
};

@inject
export class DateRange {
    @prop(() => {}) public callbackFunction: Function;
    @prop(null) timezone: string;
    @prop(false) public disabled;
    @prop(() => []) public excludeRanges: DefaultDates[];

    public range: Range = { ...INITIAL_RANGE };
    public marker: unknown | null = null;
    private $refs: {
        picker: {
            togglePicker(visible: boolean): void;
            resetPicker: () => void;
            setPredefinedRange: (date: DefaultDates) => void;
        };
    };
    private $slots: { [key: string]: HTMLElement };

    public timezoneChanged() {
        if (!this.timezone) return;

        if (this.range.startDate || this.range.endDate) {
            this.customRange(this.range);
        } else {
            this.setPredefinedRange(this.marker);
        }
    }

    private setPredefinedRange(data: unknown): void {
        this.marker = data;

        const knownRange = DefaultDates.any ? null : DEFAULT_RANGES.find((i) => i.value === data);

        this.callbackFunction({
            marker: knownRange?.getMarker(formatDate, this.timezone),
            range: knownRange?.getRange(this.timezone)
        });

        this.$refs.picker.togglePicker(false);
        this.range = { ...INITIAL_RANGE };
    }

    private customRange({ startDate, endDate }: Range): void {
        const useTimezone = this.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
        const dateEnd = moment(endDate).format("YYYY-MM-DD");
        const dateStart = moment(startDate).format("YYYY-MM-DD");
        this.range = { startDate, endDate };
        this.marker = `[${dateStart} TO ${dateEnd}]^\"${useTimezone}\"`;
        this.callbackFunction({
            marker: this.marker,
            range: this.range
        });
    }

    public resetPicker(predefinedRange = DefaultDates.any): void {
        this.setPredefinedRange(predefinedRange);
        this.range = { ...INITIAL_RANGE };
    }

    get maxDate(): Date {
        return new Date();
    }

    get hasDefaultSlot(): boolean {
        return !!this.$slots.default;
    }

    get selectedRangeName(): string {
        return DEFAULT_RANGES.find((item) => item.value === this.marker)?.label;
    }

    get selectedRangeDates(): string {
        return `${this.range?.startDate.toLocaleDateString()} - ${this.range?.endDate.toLocaleDateString()}`;
    }

    get ranges() {
        const ranges = {};

        DEFAULT_RANGES.filter(({ value }) => !this.excludeRanges.includes(value)).forEach(({ label, value }) => (ranges[label] = value));

        return ranges;
    }
}
